import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export interface CompressOptions {
  base64: string;
  quality: "Low" | "Medium" | "High" | "Original";
}

const compressionConfig = {
  High: { scale: 1.0, jpegQuality: 0.9 },
  Medium: { scale: 1.0, jpegQuality: 0.6 },
  Low: { scale: 0.7, jpegQuality: 0.3 },
};

const base64ToBytes = (base64: string): number => {
  const cleaned = base64.split(",")[1] || base64;
  return Math.ceil((cleaned.length * 3) / 4);
};

const formatSize = (bytes: number): string => {
  const kb = bytes / 1024;
  const mb = kb / 1024;

  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${Math.round(kb)} KB`;
};

export const compressPdf = async ({ base64, quality }: CompressOptions) => {
  const originalBytes = base64ToBytes(base64);

  if (quality === "Original") {
    return {
      compressedBase64: base64,
      originalSize: formatSize(originalBytes),
      compressedSize: formatSize(originalBytes),
    };
  }

  try {
    const byteCharacters = atob(base64.split(",")[1] || base64);
    const pdfBuffer = new Uint8Array(
      [...byteCharacters].map((c) => c.charCodeAt(0))
    );

    const pdf = await pdfjsLib.getDocument({ data: pdfBuffer }).promise;
    const pdfDoc = await PDFDocument.create();
    const { scale, jpegQuality } = compressionConfig[quality];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: ctx as any, viewport }).promise;

      const imgData = canvas.toDataURL("image/jpeg", jpegQuality);
      const imgBytes = await fetch(imgData).then((r) => r.arrayBuffer());

      const pdfImage = await pdfDoc.embedJpg(imgBytes);
      const newPage = pdfDoc.addPage([viewport.width, viewport.height]);
      newPage.drawImage(pdfImage, { x: 0, y: 0, width: viewport.width, height: viewport.height });
    }

    const compressedBytes = await pdfDoc.save();
    const compressedBase64 = btoa(
      compressedBytes.reduce((data, byte) => data + String.fromCharCode(byte), "")
    );

    return {
      compressedBase64: `data:application/pdf;base64,${compressedBase64}`,
      originalSize: formatSize(originalBytes),
      compressedSize: formatSize(compressedBytes.length),
    };

  } catch (err) {
    console.error("PDF compression failed:", err);
    return {
      compressedBase64: base64,
      originalSize: formatSize(originalBytes),
      compressedSize: formatSize(originalBytes),
    };
  }
};
